import { Directive, ElementRef, HostListener, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Separador de miles VISUAL para inputs de monto, sin tocar el valor que viaja.
 *
 * El input DEBE ser `type="text"`: por spec, `input[type=number]` sanea su valor
 * a string vacío cuando el contenido no es un *valid floating-point number*, así
 * que con separadores el navegador lo vacía. Se acompaña con
 * `inputmode="numeric"` para no perder el teclado numérico en móvil.
 *
 * Contrato:
 *  - al modelo emite SIEMPRE `number` (o `null` si el campo está vacío) — nunca
 *    string, así el monto sigue siendo comparable y divisible aguas arriba;
 *  - en pantalla muestra el número con comas cada 3 dígitos (misma vara que el
 *    resto de la app: "331,556 COP", "9,619.67 USDT");
 *  - `decimales` acota cuántos decimales se pueden TIPEAR: 0 para monedas sin
 *    centavos (CLP, VES, COP), 2 para USD. Con 0 se descarta el punto tanto al
 *    tipear como al pegar.
 *
 * Distinta a propósito de `matInputCommifiedDirective`, que emite string,
 * formatea sólo en `blur` y rompe el formato con 3+ decimales (aplica la regex
 * de miles a todo el string, incluida la parte decimal). Ésa la usan ~14
 * pantallas y no se toca.
 *
 * Uso:
 *   <input type="text" inputmode="numeric" appMonto [decimales]="0"
 *          [(ngModel)]="montoClp" (ngModelChange)="onMontoClpChange()">
 */
@Directive({
  selector: 'input[appMonto]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MontoFormateadoDirective),
      multi: true,
    },
  ],
})
export class MontoFormateadoDirective implements ControlValueAccessor {
  /** Decimales tipeables. 0 = sólo enteros. */
  @Input() decimales = 0;

  private alCambiar: (v: number | null) => void = () => {};
  private alTocar: () => void = () => {};

  constructor(private readonly el: ElementRef<HTMLInputElement>) {}

  // ─────────── ControlValueAccessor ───────────

  writeValue(v: unknown): void {
    const n = v === null || v === undefined || v === '' ? null : Number(v);
    this.el.nativeElement.value =
      n === null || !Number.isFinite(n) ? '' : this.formatear(this.aTexto(n));
  }

  registerOnChange(fn: (v: number | null) => void): void {
    this.alCambiar = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.alTocar = fn;
  }

  setDisabledState(deshabilitado: boolean): void {
    this.el.nativeElement.disabled = deshabilitado;
  }

  @HostListener('blur')
  onBlur(): void {
    this.alTocar();
  }

  /**
   * Reformatea en vivo y repone el caret. La posición se recalcula contando
   * caracteres SIGNIFICATIVOS (dígitos y punto) a la izquierda, no caracteres
   * crudos: si se contaran crudos, el caret saltaría cada vez que aparece o
   * desaparece una coma.
   */
  @HostListener('input')
  onInput(): void {
    const input = this.el.nativeElement;
    const caret = input.selectionStart ?? input.value.length;
    const signifIzquierda = this.significativos(input.value.slice(0, caret)).length;

    const limpio = this.limpiar(input.value);
    const formateado = this.formatear(limpio);

    input.value = formateado;
    const pos = this.posicionCaret(formateado, signifIzquierda);
    input.setSelectionRange(pos, pos);

    const n = Number(limpio);
    this.alCambiar(limpio === '' || limpio === '.' || !Number.isFinite(n) ? null : n);
  }

  // ─────────── Helpers ───────────

  /**
   * Deja sólo dígitos (y un punto decimal si `decimales > 0`), acota los
   * decimales al máximo permitido y saca ceros a la izquierda. El signo menos se
   * descarta: no hay montos negativos.
   */
  private limpiar(raw: string): string {
    const validos = raw.replace(this.decimales > 0 ? /[^\d.]/g : /[^\d]/g, '');
    if (this.decimales === 0) return this.sinCerosIzquierda(validos);

    const [entero, ...resto] = validos.split('.');
    const enteroLimpio = this.sinCerosIzquierda(entero);
    if (!validos.includes('.')) return enteroLimpio;
    const decimal = resto.join('').slice(0, this.decimales);
    return `${enteroLimpio}.${decimal}`;
  }

  /** "007" → "7"; "0" y "" quedan igual. */
  private sinCerosIzquierda(s: string): string {
    return s.replace(/^0+(?=\d)/, '');
  }

  /**
   * Comas cada 3 dígitos, SÓLO en la parte entera. Aplicar la regex al string
   * completo es el bug de la directiva vieja: "1234.5678" → "1,234.5,678".
   */
  private formatear(limpio: string): string {
    if (limpio === '') return '';
    const [entero, decimal] = limpio.split('.');
    const conComas = entero.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimal !== undefined ? `${conComas}.${decimal}` : conComas;
  }

  private significativos(s: string): string {
    return s.replace(/[^\d.]/g, '');
  }

  /** Índice en `formateado` justo después del n-ésimo carácter significativo. */
  private posicionCaret(formateado: string, n: number): number {
    if (n <= 0) return 0;
    let vistos = 0;
    for (let i = 0; i < formateado.length; i++) {
      if (/[\d.]/.test(formateado[i])) {
        vistos++;
        if (vistos === n) return i + 1;
      }
    }
    return formateado.length;
  }

  /** Número → texto sin notación científica ni cola de flotante. */
  private aTexto(n: number): string {
    const factor = 10 ** this.decimales;
    const redondeado = Math.round(n * factor) / factor;
    return this.decimales > 0
      ? String(redondeado)
      : String(Math.trunc(redondeado));
  }
}
